import React from 'react';
import styled from 'styled-components';
import {
    FloatingActionMenu,
    Checkbox,
    Text,
    Container,
    Box,
    Popover,
    palette,
    spacing,
} from '@landr/maestro';
import { Trans, t } from '@lingui/macro';
import { useMatch } from 'react-router-dom';
import AddSampleToCollectionButton from 'components/CommonUi/AddSampleToCollectionButton';
import { useSelectedSamplesContext } from 'contexts/SelectedSamplesContext/SelectedSamplesContext';
import { getSamplesTableSizes } from 'components/SamplesTable/utils/getSamplesTableSizes';
import { useToLocalizedPath } from 'hooks/useToLocalizedPath/useToLocalizedPath';
import useGetCurrentBreakpoint from 'hooks/useGetCurrentBreakpoint/useGetCurrentBreakpoint';
import { useCreatorContext } from 'controllers/business/Creator/Creator.controller';
import { useSidebarLayoutContext } from 'controllers/SidebarLayout/SidebarLayout';
import { getIsDAWPlugin } from 'helpers/executionEnvironment';
import { DownloadButton } from '../CommonUi/DownloadButton';
import { RoutePaths } from '../../types/enums';
import DeleteCollectionSampleButton from '../DeleteCollectionSampleButton';

export const StyledPopover = styled(Popover)`
    color: ${palette('neutral.0')};
    padding-top: ${spacing('xs')};
    padding-bottom: ${spacing('xs')};
`;

const SelectedCount = styled(Text)`
    display: block;
    widows: 100%;
    margin-right: ${({ theme }) => theme.spacing(5)};
`;

const OffsetFloatingWrapper = styled.div<{
    isCreatorVisible: boolean;
}>`
    position: fixed;
    bottom: ${({ isCreatorVisible, theme }) => {
        const bottomSpacing = spacing('sm')({ theme });
        const creatorHeight = '80px';

        return isCreatorVisible
            ? `calc(${bottomSpacing} + ${creatorHeight})`
            : bottomSpacing;
    }};
    background-color: transparent;
    width: auto;
    max-width: 100%;
    transition: bottom 300ms ease-in-out;
    transform: translateX(-12px);
`;

export const FloatingActionsMenu: React.FC = () => {
    const { isVisible: isCreatorVisible } = useCreatorContext();
    const isDAWPlugin = getIsDAWPlugin();

    const { samples: selectedSamples, clearSamplesSelection } =
        useSelectedSamplesContext();

    const { isSidebarOpen } = useSidebarLayoutContext();
    const currentBreakpoint = useGetCurrentBreakpoint();
    const { isSm } = getSamplesTableSizes({
        isSidebarOpen,
        currentBreakpoint,
    });

    const isCollectionDetailsPage = useMatch(`/${RoutePaths.Collections}/:id`);

    const toLocalizedPath = useToLocalizedPath();

    const routesToHideMenu = [toLocalizedPath(`/${RoutePaths.Collections}`)];

    const isFloatingActionsMenuVisible = !routesToHideMenu.some(
        (route) => route === location.pathname,
    );

    if (!isFloatingActionsMenuVisible || isSm || !selectedSamples.length) {
        return null;
    }

    return (
        <Container>
            <Box>
                <OffsetFloatingWrapper
                    data-component="FloatingActionMenu"
                    isCreatorVisible={isCreatorVisible}
                >
                    <FloatingActionMenu>
                        <Checkbox
                            label={t`Unselect all sounds`}
                            hideLabel
                            isChecked
                            onChange={clearSamplesSelection}
                            data-testid="toast-checkbox"
                        />
                        <SelectedCount>
                            {`${selectedSamples.length}`}
                            &nbsp;<Trans>selected</Trans>
                        </SelectedCount>
                        <AddSampleToCollectionButton
                            samples={selectedSamples}
                            source={{ interaction: 'Multi-select' }}
                            variant="subtle"
                            isRounded
                            size="sm"
                            popoverPlacement="bottom-start"
                        />
                        {isCollectionDetailsPage && (
                            <DeleteCollectionSampleButton
                                samples={selectedSamples}
                                variant="subtle"
                                aria-label={t`Remove from collection`}
                                label={t`Remove from collection`}
                                isRounded
                                size="sm"
                            />
                        )}
                        {!isDAWPlugin && (
                            <DownloadButton
                                item={selectedSamples.map(
                                    (sample) => sample.guid,
                                )}
                                variant="subtle"
                                isRounded
                                size="sm"
                            />
                        )}
                    </FloatingActionMenu>
                </OffsetFloatingWrapper>
            </Box>
        </Container>
    );
};

export default FloatingActionsMenu;
